import { Label } from "@/components/ui/label";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Eye, EyeOff, RefreshCcw } from "lucide-react";
import { useBlockbrainCredentials } from "@/hooks/useBlockbrainCredentials";
import { useTranslation } from "react-i18next";
import { Loading } from "@/components/ui/loading";
import { ControlWrapper } from "@/components/ui/control-wrapper";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/components/ui/select";

export function BlockbrainCredentialsInput() {
  const { t } = useTranslation();
  const {
    apiKey,
    apiUrl,
    botId,
    isLoading,
    isTesting,
    testResult,
    showApiKey,
    bots,
    isLoadingBots,
    botsError,
    handleApiKeyChange,
    handleApiUrlChange,
    handleBotIdChange,
    toggleApiKeyVisibility,
    handleSaveCredentials,
    handleTestConnection,
    handleLoadBots,
    setLocalBotId,
  } = useBlockbrainCredentials();

  return (
    <div className="space-y-4">
      <ControlWrapper>
        <div className="flex items-center justify-between">
          <Label htmlFor="blockbrain-api-key">{t("blockbrain.apiKey")}</Label>
        </div>
        <form
          onSubmit={e => {
            e.preventDefault();
            handleSaveCredentials();
          }}
        >
          <div className="flex gap-2">
            <Input
              id="blockbrain-api-key"
              type={showApiKey ? "text" : "password"}
              value={apiKey}
              onChange={handleApiKeyChange}
              disabled={isLoading}
              placeholder={t("blockbrain.apiKeyPlaceholder")}
              className="flex-1"
              autoComplete="off"
            />
            <Button
              type="button"
              onClick={toggleApiKeyVisibility}
              variant="ghost"
              size="icon"
              disabled={isLoading}
            >
              {showApiKey ? <EyeOff className="h-4 w-4" /> : <Eye className="h-4 w-4" />}
            </Button>
          </div>
        </form>
        {isLoading && <Loading />}
      </ControlWrapper>

      <ControlWrapper>
        <div className="flex items-center justify-between">
          <Label htmlFor="blockbrain-api-url">{t("blockbrain.apiUrl")}</Label>
        </div>
        <Input
          id="blockbrain-api-url"
          type="text"
          value={apiUrl}
          onChange={handleApiUrlChange}
          disabled={isLoading}
          placeholder={t("blockbrain.apiUrlPlaceholder")}
        />
        <p className="text-xs text-muted-foreground mt-1">
          Standard: https://blocky.theblockbrain.ai
        </p>
      </ControlWrapper>

      <ControlWrapper>
        <div className="flex items-center justify-between">
          <Label htmlFor="blockbrain-bot-id">{t("blockbrain.botId")}</Label>
          <Button
            type="button"
            variant="ghost"
            size="sm"
            onClick={handleLoadBots}
            disabled={isLoading || isLoadingBots || !apiKey}
            className="h-7"
          >
            <RefreshCcw className={`h-3 w-3 mr-1 ${isLoadingBots ? "animate-spin" : ""}`} />
            {isLoadingBots ? t("blockbrain.loadingBots") : t("blockbrain.loadBots")}
          </Button>
        </div>

        {bots.length > 0 && (
          <Select value={botId} onValueChange={setLocalBotId} disabled={isLoading}>
            <SelectTrigger>
              <SelectValue placeholder={t("blockbrain.selectBot")} />
            </SelectTrigger>
            <SelectContent>
              {bots.map(bot => (
                <SelectItem key={bot.id} value={bot.id}>
                  {bot.name}
                  {bot.botTemplate ? (
                    <span className="text-muted-foreground text-xs ml-2">({bot.botTemplate})</span>
                  ) : null}
                </SelectItem>
              ))}
            </SelectContent>
          </Select>
        )}

        <Input
          id="blockbrain-bot-id"
          type="text"
          value={botId}
          onChange={handleBotIdChange}
          disabled={isLoading}
          placeholder={t("blockbrain.botIdPlaceholder")}
        />

        {botsError && (
          <p className="text-xs text-red-600 mt-1">
            {t("blockbrain.botsError")}: {botsError}
          </p>
        )}
        {bots.length === 0 && !botsError && (
          <p className="text-xs text-muted-foreground mt-1">
            {t("blockbrain.botIdHint")}
          </p>
        )}
      </ControlWrapper>

      <div className="flex items-center gap-3">
        <Button
          type="button"
          variant="outline"
          onClick={handleTestConnection}
          disabled={isLoading || isTesting || !apiKey}
        >
          {isTesting ? t("blockbrain.testing") : t("blockbrain.testConnection")}
        </Button>
        {testResult && (
          <span
            className={`text-sm ${testResult.ok ? "text-green-600" : "text-red-600"}`}
            data-testid="blockbrain-test-result"
          >
            {testResult.ok
              ? t("blockbrain.testSuccess")
              : `${t("blockbrain.testFailed")}: ${testResult.error || testResult.status || ""}`}
          </span>
        )}
      </div>
    </div>
  );
}
